/**
 * Area `account` of the console gateway — owned by plan-002 unit **RB-17** (design §15.4).
 *
 *   signUp          `signUpPerson`              a person AND their business (§15.4.2)
 *   createAccount   `createAccount`             "Finish setting up your business" (the recovery path)
 *   switchAccount   `switchAuthSessionAccount`  re-scope to another account (§15.4.4)
 *
 * ── WHAT SIGN-UP SENDS NOW (C20) ─────────────────────────────────────────────────────────
 * The business name, the country and the time zone travel with the person: sign-up creates the
 * account in the same request (RB-17). The country is the person's own choice from the closed
 * list in `@wetalk/domain` — the console never proposes one — and the time zone is the browser's
 * own, shown to the person for confirmation before it is sent. Neither field is optional, so no
 * sign-up can leave without them.
 *
 * ── WHAT A SIGN-UP ANSWERS (AMENDED 2026-10-17, ADR 0071: verification-first) ───────────────
 *   · `202 verification_sent` — the API's answer: the person exists, their verification mail is
 *     on its way, nobody is signed in and no business exists yet: `{ kind: "verification_sent" }`.
 *     The person opens the link, logs in and finishes setting up the business (`createAccount`);
 *   · `201` with an auth_session — `{ kind: "signed_in" }`: the sandbox gateway's answer (it sends
 *     no email, so it founds the business at once) and an API from before 2026-10-17. The session
 *     goes through `context.adoptAuthSession`, the same decoding log-in uses, so it may still raise
 *     `AuthSessionUnscopedError` (no choices: `createAccount` next; one: `switchAccount` next).
 *
 * ── IDEMPOTENCY ──────────────────────────────────────────────────────────────────────────
 * `createAccount` requires an `Idempotency-Key` (§15.4.2). The KEY is the screen's to choose,
 * because only the screen knows when a click is a retry of the same intent and when it is a new
 * one (the person changed the name): the same key for the same form contents, a new key when
 * they change. The slice sends exactly what it is given.
 *
 * `accountGap` is empty: every account operation is documented and implemented here.
 *
 * ── PLAN-014 B1: CREATE AN ORGANIZATION (design §27.9.2, FR12-ONB-01/05) ────────────────────
 *   onboardingDraft       `getOnboardingDraft`    the server-side draft, with what the screen shows
 *   saveOnboardingDraft   `saveOnboardingDraft`   validated inputs and the person's own step markers
 *   createOrganization    `createAccount`         the B1 body: name, country, first workspace, terms
 * The draft lives on the server (FR12-ONB-05): nothing of it is kept in browser storage. The
 * `Idempotency-Key` of `createOrganization` is the screen's, for the reason given above.
 *
 * ── HOW THIS FILE WORKS (IC-2) ───────────────────────────────────────────────────────────
 * The method signatures below are merged onto `ConsoleGateway` by declaration merging.
 * `context.adoptAuthSession` learns the CSRF token from every auth_session body and returns the
 * console's view, raising `AuthSessionUnscopedError` exactly as the identity methods in
 * `api-gateway.ts` do — the same function, handed in.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import { MEMBER_ROLE, type SupportedCountry } from "@wetalk/domain";

import { entryGuardBodyOf, type AuthSessionView, type ConsoleGateway, type EntryGuardView, type WorkspaceRoleView } from "../gateway.js";
import { entryGuardOrSolved } from "../identity-challenge.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

/** What the sign-up screen collects, before anything is created. Every field is required. */
export interface SignUpRequestView {
  readonly accountName: string;
  readonly emailAddress: string;
  readonly password: string;
  /** ISO 3166-1 alpha-2, chosen by the person from `SUPPORTED_COUNTRY`. Never proposed. */
  readonly country: SupportedCountry;
  /** An IANA zone, proposed from the browser and confirmed by the person. */
  readonly timeZone: string;
  /** ADR 0071: the entry card's trap value and solved identity challenge. */
  readonly guard?: EntryGuardView;
}

/** What a sign-up answered (ADR 0071; see the header). */
export type SignUpOutcomeView =
  | { readonly kind: "verification_sent" }
  | { readonly kind: "signed_in"; readonly authSession: AuthSessionView };

/** What "Finish setting up your business" collects. */
export interface CreateAccountRequestView {
  readonly accountName: string;
  readonly country: SupportedCountry;
  readonly timeZone: string;
  /**
   * The `Idempotency-Key`: the same for a retry of the same form contents, new when they change.
   * The screen owns it (see the header).
   */
  readonly idempotencyKey: string;
}

/** What an invitation link joins — the join page's first read (`inspectInvitation`). */
export interface InvitationPreviewView {
  readonly accountName: string;
  readonly workspaceName: string;
  /**
   * The workspace role it grants — any of the seven (plan-014 A3's presets included). AMENDED 2026-10-06 (plan-014
   * B2, A4's note): this read refused developer, campaign manager and billing invitations as "not a member role".
   */
  readonly role: WorkspaceRoleView;
  /** The address it was sent to: the person joins with it and cannot change it. */
  readonly emailAddress: string;
  readonly expiresAt: string;
  /**
   * Whether that address already signs in to WeTalk (review #143): the join page then asks for
   * its password rather than offering to set a new one.
   */
  readonly signInExists: boolean;
}

/**
 * Joining from a link. Name and password only for a browser with no session (a new person). AMENDED 2026-11-03 (design
 * §16.37 amendment): or name and `code` — the code `requestInvitationSignInCode` mailed to the invitation's address —
 * and no password. Never a password and a code together.
 */
export interface AcceptInvitationRequestView {
  readonly token: string;
  readonly displayName: string | null;
  readonly password: string | null;
  readonly code: string | null;
}

/** The three steps of Create an organization (FR12-ONB-01). */
export type OnboardingStepView = "identity" | "organization" | "voice_agent";

/** Why an organization created now would or would not receive the person's one trial. */
export type TrialEligibilityView = "eligible" | "email_unverified" | "already_claimed" | "legacy_trial" | "person_unknown";

/** The draft's validated inputs; `null` while the person has not typed one. */
export interface OnboardingInputView {
  readonly accountName: string | null;
  readonly country: string | null;
  readonly firstWorkspaceName: string | null;
}

/** `GET /v1/me/onboarding-draft` — the draft and what the screen shows beside it. */
export interface OnboardingDraftView {
  readonly stepCompleted: readonly OnboardingStepView[];
  readonly input: OnboardingInputView;
  /** The organization the draft created, once its organization step committed. */
  readonly accountId: string | null;
  readonly completedAt: string | null;
  readonly emailAddress: string;
  readonly emailVerified: boolean;
  readonly trialEligibility: TrialEligibilityView;
  /** How long a trial is, in seconds (the configured length). */
  readonly trialGrantSecond: number;
  /** The billing timezone a new organization gets — shown, never chosen. */
  readonly billingTimeZone: string;
  readonly terms: { readonly revision: string; readonly termsUrl: string; readonly privacyUrl: string };
}

/** What a save sends: the person's own markers and the inputs. */
export interface SaveOnboardingDraftRequestView {
  readonly stepCompleted: readonly OnboardingStepView[];
  readonly input: OnboardingInputView;
}

/** Create an organization (the B1 body of `createAccount`). */
export interface CreateOrganizationRequestView {
  readonly accountName: string;
  readonly country: SupportedCountry;
  readonly firstWorkspaceName: string;
  /** The terms revision the person accepted on the screen. */
  readonly termsRevision: string;
  /** 16–128 characters; the same for a retry of the same form contents, new when they change. */
  readonly idempotencyKey: string;
}

/** What creating an organization answers: the auth_session now scoped to it, and the trial outcome. */
export interface CreatedOrganizationView {
  readonly authSession: AuthSessionView;
  readonly accountId: string;
  readonly workspaceId: string;
  readonly trialGranted: boolean;
}

const ONBOARDING_SCHEMA_REVISION = 1;
const ONBOARDING_STEP: readonly OnboardingStepView[] = ["identity", "organization", "voice_agent"];
const TRIAL_ELIGIBILITY: readonly TrialEligibilityView[] = [
  "eligible",
  "email_unverified",
  "already_claimed",
  "legacy_trial",
  "person_unknown",
];

function textOrNull(value: unknown, field: string): string | null {
  if (value === null) return null;
  if (typeof value !== "string") throw new Error(`getOnboardingDraft: ${field} is not a string`);
  return value;
}

/** Reads `OnboardingDraft` off the wire; anything unexpected is a contract fault, raised by name. */
export function onboardingDraftViewOf(body: unknown, operation: string): OnboardingDraftView {
  const data = (body as { data?: unknown }).data as Record<string, unknown> | undefined;
  if (data === undefined || data === null) throw new Error(`${operation}: no data`);
  const step = data["step_completed"];
  const input = data["input"] as Record<string, unknown> | undefined;
  const person = data["person"] as Record<string, unknown> | undefined;
  const trial = data["trial"] as Record<string, unknown> | undefined;
  const terms = data["terms"] as Record<string, unknown> | undefined;
  const eligibility = trial?.["eligibility"];
  if (
    !Array.isArray(step) ||
    input === undefined ||
    person === undefined ||
    trial === undefined ||
    terms === undefined ||
    typeof eligibility !== "string" ||
    !(TRIAL_ELIGIBILITY as readonly string[]).includes(eligibility) ||
    typeof trial["granted_second"] !== "number" ||
    typeof data["billing_timezone"] !== "string"
  ) {
    throw new Error(`${operation}: the draft is not shaped as documented`);
  }
  return {
    stepCompleted: ONBOARDING_STEP.filter((one) => (step as readonly unknown[]).includes(one)),
    input: {
      accountName: textOrNull(input["account_name"], "input.account_name"),
      country: textOrNull(input["country"], "input.country"),
      firstWorkspaceName: textOrNull(input["first_workspace_name"], "input.first_workspace_name"),
    },
    accountId: textOrNull(data["account_id"], "account_id"),
    completedAt: textOrNull(data["completed_at"], "completed_at"),
    emailAddress: String(person["email"]),
    emailVerified: person["email_verified"] === true,
    trialEligibility: eligibility as TrialEligibilityView,
    trialGrantSecond: trial["granted_second"],
    billingTimeZone: data["billing_timezone"],
    terms: {
      revision: String(terms["revision"]),
      termsUrl: String(terms["terms_url"]),
      privacyUrl: String(terms["privacy_url"]),
    },
  };
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** The signed-in person's Create-an-organization draft (plan-014 B1), with what the screen shows. */
    onboardingDraft(): Promise<OnboardingDraftView>;
    /** Saves the draft's inputs and the person's own step markers; answers it as saved. */
    saveOnboardingDraft(request: SaveOnboardingDraftRequestView): Promise<OnboardingDraftView>;
    /**
     * Creates an organization for the signed-in person (plan-014 B1) and scopes the session to it, as
     * its Owner. The same key re-answers the same organization.
     */
    createOrganization(request: CreateOrganizationRequestView): Promise<CreatedOrganizationView>;
    /**
     * Creates the person; their business waits for their verified address (ADR 0071) —
     * `verification_sent`. `signed_in` (the sandbox, an older API) may still raise
     * `AuthSessionUnscopedError` as log-in does.
     */
    signUp(request: SignUpRequestView): Promise<SignUpOutcomeView>;
    /** Creates a business for the signed-in person and scopes the session to it, as owner. */
    createAccount(request: CreateAccountRequestView): Promise<AuthSessionView>;
    /**
     * Re-scopes the session to another account the person belongs to — `workspaceId: null` for
     * that account's first workspace. The session is rotated; the new CSRF token is learned.
     */
    switchAccount(accountId: string, workspaceId: string | null): Promise<AuthSessionView>;
    /** What an invitation link joins, before anyone signs in (design §16.37). */
    inspectInvitation(token: string): Promise<InvitationPreviewView>;
    /**
     * Joins the invitation's workspace — as the signed-in person, or as a new person with the
     * name and password given — and scopes the session to it.
     */
    acceptInvitation(request: AcceptInvitationRequestView): Promise<AuthSessionView>;
  }
}

export const accountGap: readonly GatewayGap[] = [];

export function accountApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<
  ConsoleGateway,
  | "signUp"
  | "createAccount"
  | "switchAccount"
  | "inspectInvitation"
  | "acceptInvitation"
  | "onboardingDraft"
  | "saveOnboardingDraft"
  | "createOrganization"
> {
  return {
    async onboardingDraft(): Promise<OnboardingDraftView> {
      return onboardingDraftViewOf(assertOk(await client.getOnboardingDraft()), "getOnboardingDraft");
    },

    async saveOnboardingDraft(request: SaveOnboardingDraftRequestView): Promise<OnboardingDraftView> {
      const saved = await client.saveOnboardingDraft({
        body: {
          schema_revision: ONBOARDING_SCHEMA_REVISION,
          step_completed: request.stepCompleted,
          input: {
            account_name: request.input.accountName,
            country: request.input.country,
            first_workspace_name: request.input.firstWorkspaceName,
          },
        },
      });
      return onboardingDraftViewOf(assertOk(saved), "saveOnboardingDraft");
    },

    async createOrganization(request: CreateOrganizationRequestView): Promise<CreatedOrganizationView> {
      const created = assertOk(
        await client.createAccount({
          body: {
            account_name: request.accountName,
            country: request.country,
            first_workspace_name: request.firstWorkspaceName,
            terms_revision: request.termsRevision,
          },
          header: { "idempotency-key": request.idempotencyKey },
        }),
      );
      const data = (created as { data: Record<string, unknown> }).data;
      const account = data["account"] as { account_id?: unknown } | undefined;
      const workspace = data["workspace"] as { workspace_id?: unknown } | undefined;
      if (typeof account?.account_id !== "string" || typeof workspace?.workspace_id !== "string") {
        throw new Error("createAccount: the created organization is not named in the answer");
      }
      return {
        authSession: await context.adoptAuthSession(created, "createAccount"),
        accountId: account.account_id,
        workspaceId: workspace.workspace_id,
        trialGranted: data["trial_granted"] === true,
      };
    },

    async signUp(request: SignUpRequestView): Promise<SignUpOutcomeView> {
      const created = await client.signUpPerson({
        body: {
          email: request.emailAddress,
          password: request.password,
          display_name: displayNameOfAddress(request.emailAddress),
          account_name: request.accountName,
          country: request.country,
          time_zone: request.timeZone,
          // ADR 0071: a caller that passes no guard still sends a solved challenge.
          ...entryGuardBodyOf(await entryGuardOrSolved(request.guard, client)),
        },
      });
      const answered = assertOk(created);
      if (created.status === 202) return { kind: "verification_sent" };
      return { kind: "signed_in", authSession: await context.adoptAuthSession(answered, "signUpPerson") };
    },

    async createAccount(request: CreateAccountRequestView): Promise<AuthSessionView> {
      const created = await client.createAccount({
        body: {
          account_name: request.accountName,
          country: request.country,
          time_zone: request.timeZone,
        },
        header: { "idempotency-key": request.idempotencyKey },
      });
      return context.adoptAuthSession(assertOk(created), "createAccount");
    },

    async switchAccount(accountId: string, workspaceId: string | null): Promise<AuthSessionView> {
      const switched = await client.switchAuthSessionAccount({
        body: { account_id: accountId, workspace_id: workspaceId },
      });
      return context.adoptAuthSession(assertOk(switched), "switchAuthSessionAccount");
    },

    async inspectInvitation(token: string): Promise<InvitationPreviewView> {
      const found = assertOk(await client.inspectInvitation({ body: { token } }));
      const data = (found as { data: Record<string, unknown> }).data;
      const role = data["role"];
      if (typeof role !== "string" || !(MEMBER_ROLE as readonly string[]).includes(role)) {
        throw new Error(`inspectInvitation: "${String(role)}" is not a member role`);
      }
      return {
        accountName: String(data["account_name"]),
        workspaceName: String(data["workspace_name"]),
        role: role as WorkspaceRoleView,
        emailAddress: String(data["email"]),
        expiresAt: String(data["expires_at"]),
        signInExists: data["sign_in_exists"] === true,
      };
    },

    async acceptInvitation(request: AcceptInvitationRequestView): Promise<AuthSessionView> {
      const joined = await client.acceptInvitation({
        body: {
          token: request.token,
          ...(request.displayName === null ? {} : { display_name: request.displayName }),
          ...(request.password === null ? {} : { password: request.password }),
          ...(request.code === null ? {} : { code: request.code }),
        },
      });
      return context.adoptAuthSession(assertOk(joined), "acceptInvitation");
    },
  };
}

/**
 * A display name from an address's local part — `giulia.rossi@…` → `Giulia Rossi` — the same
 * derivation the sandbox uses. Sign-up needs a person's name and the screen collects none.
 */
function displayNameOfAddress(emailAddress: string): string {
  const at = emailAddress.indexOf("@");
  const local = at > 0 ? emailAddress.slice(0, at) : emailAddress;
  const word = local
    .split(/[._-]/)
    .filter((one) => one !== "")
    .map((one) => one.slice(0, 1).toUpperCase() + one.slice(1));
  return word.length > 0 ? word.join(" ") : local;
}
